import React, { useMemo, useCallback, useState } from 'react'
import { Button } from '@/components/ui/button'
import { ChevronRight, Pause, Play } from 'lucide-react'
import { useGamePhase } from '@/hooks/useGamePhase'
import {
  TimerMode,
  TimerModeLabels,
} from '@/components/settings/timer-configuration/types'
import { useParams } from 'react-router-dom'
import { useRoomControls } from '@/hooks/useRoomControls'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'
import { TimerDisplay } from '../settings/timer-configuration/TimerDisplay'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip'
import { Label } from '@radix-ui/react-dropdown-menu'
import { Switch } from '@/components/ui/switch'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { updateRoomDetails } from '@/redux/asyncThunks/room'
import { toast } from 'sonner'
import { CharterRoundControlPanel } from '@/features/charter/components/Professor/CharterRoundControlPanel'

enum Phase {
  RANDR = 'randr',
  ADVISOR = 'advisor',
  NEGOTIATION = 'negotiation',
  DECISION = 'decision',
}

const PHASE_CONFIG: Record<Phase, { label: string; color: string }> = {
  [Phase.RANDR]: {
    label: 'R&R',
    color: 'from-blue-500 to-blue-600',
  },
  [Phase.ADVISOR]: {
    label: 'Advisor',
    color: 'from-purple-500 to-purple-600',
  },
  [Phase.NEGOTIATION]: {
    label: 'Conference',
    color: 'from-amber-500 to-orange-500',
  },
  [Phase.DECISION]: {
    label: 'Decision',
    color: 'from-emerald-500 to-green-600',
  },
}

const ROUND_COLOR_SCHEMES = {
  1: {
    primary: 'from-indigo-500 to-indigo-600',
    secondary: 'from-indigo-400 to-indigo-500',
    accent: 'indigo-500',
  },
  2: {
    primary: 'from-purple-500 to-purple-600',
    secondary: 'from-purple-400 to-purple-500',
    accent: 'purple-500',
  },
  3: {
    primary: 'from-emerald-500 to-emerald-600',
    secondary: 'from-emerald-400 to-emerald-500',
    accent: 'emerald-500',
  },
  4: {
    primary: 'from-rose-500 to-rose-600',
    secondary: 'from-rose-400 to-rose-500',
    accent: 'rose-500',
  },
} as const

const GameControlPanel: React.FC = () => {
  const {
    totalRounds,
    gameMode,
    manualMode,
    currentRound,
    currentPhase,
    phaseId,
    roundId,
    isGameCompleted,
    isGameStarted,
    phaseStatus,
    isInfinitePhase,
    roundStatus,
    endTime,
  } = useGamePhase()
  const params = useParams()
  const roomId = params.roomId as string
  const effectiveCurrentRound = currentRound > 0 ? currentRound : 1
  const { handleStartNextRound, handleEndPhase, handlePauseGame } =
    useRoomControls(roomId, phaseId, roundId, roundStatus)

  const dispatch = useAppDispatch()
  const currentRoom = useAppSelector((state) => state.room.currentRoom)
  const [isUpdatingAutoApprove, setIsUpdatingAutoApprove] = useState(false)
  const handleStartNextRoundWrapper = () => {
    handleStartNextRound(isGameCompleted)
  }

  const handleEndPhaseWrapper = () => {
    handleEndPhase(isGameCompleted)
  }

  const handlePauseGameWrapper = () => {
    handlePauseGame(isGameCompleted)
  }

  const handleAutoApproveToggle = async (checked: boolean) => {
    if (isGameCompleted) return

    setIsUpdatingAutoApprove(true)
    try {
      await dispatch(
        updateRoomDetails({
          roomId,
          auto_approve: checked,
        })
      ).unwrap()

      toast.success(
        `Auto-approval ${checked ? 'enabled' : 'disabled'} successfully!`
      )
    } catch (error) {
      toast.error('Failed to update auto-approval setting')
    } finally {
      setIsUpdatingAutoApprove(false)
    }
  }

  const createPhase = useCallback(
    (phase: Phase) => ({
      id: phase,
      ...PHASE_CONFIG[phase],
    }),
    []
  )

  const stages = useMemo(() => {
    if (manualMode) {
      return []
    }

    if (gameMode === TimerMode.SINGLE_ROUND) {
      return [
        createPhase(Phase.RANDR),
        createPhase(Phase.ADVISOR),
        createPhase(Phase.NEGOTIATION),
        createPhase(Phase.DECISION),
      ]
    }

    if (gameMode === TimerMode.SINGLE_ROUND_ADVISOR_ONLY) {
      return [createPhase(Phase.ADVISOR)]
    }

    if (gameMode === TimerMode.MULTI_ROUND_ALL) {
      if (effectiveCurrentRound === 1) {
        return [
          createPhase(Phase.RANDR),
          createPhase(Phase.ADVISOR),
          createPhase(Phase.NEGOTIATION),
          createPhase(Phase.DECISION),
        ]
      }
      return [
        createPhase(Phase.ADVISOR),
        createPhase(Phase.NEGOTIATION),
        createPhase(Phase.DECISION),
      ]
    }

    if (gameMode === TimerMode.MULTI_ROUND_NEGOTIATION_LOCKED) {
      if (effectiveCurrentRound === 1) {
        return [
          createPhase(Phase.RANDR),
          createPhase(Phase.ADVISOR),
          createPhase(Phase.NEGOTIATION),
          createPhase(Phase.DECISION),
        ]
      }
      return [createPhase(Phase.ADVISOR), createPhase(Phase.DECISION)]
    }

    return []
  }, [manualMode, gameMode, effectiveCurrentRound, createPhase])

  const roundColors = useMemo(() => {
    return (
      ROUND_COLOR_SCHEMES[
        effectiveCurrentRound as keyof typeof ROUND_COLOR_SCHEMES
      ] || ROUND_COLOR_SCHEMES[3]
    )
  }, [effectiveCurrentRound])

  const roundNumbers = useMemo(
    () => Array.from({ length: totalRounds }, (_, i) => i + 1),
    [totalRounds]
  )

  return (
    <div className='p-2 mx-auto w-full max-w-2xl bg-gradient-to-br rounded-xl border shadow-lg from-slate-50 to-slate-100 border-slate-200'>
      <div className='flex flex-col gap-2'>
        {/* Round 0 — Pre-Game Charter controls (only shown when enabled). */}
        <CharterRoundControlPanel roomId={roomId} compact />

        <div className='flex items-center space-x-1 rounded-lg'>
          <Label className='text-xs font-medium text-slate-700'>
            Auto-Approval
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className='flex-shrink-0 pt-1'>
                  <Switch
                    checked={currentRoom?.auto_approve ?? false}
                    onCheckedChange={handleAutoApproveToggle}
                    disabled={isGameCompleted || isUpdatingAutoApprove}
                    className='bg-gray-400'
                  >
                    <span className='block h-3 w-3 translate-x-[2px] rounded-full bg-white shadow-lg transition-transform duration-300 ease-in-out data-[state=checked]:translate-x-[22px]' />
                  </Switch>
                </div>
              </TooltipTrigger>
              <TooltipContent className='px-3 max-w-xs text-sm text-white rounded-md shadow-xl bg-slate-800'>
                <p>
                  Enable or disable auto approval for player actions during the
                  scenario. When enabled, all player actions will be
                  automatically approved without manual review.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className='flex gap-2 items-center'>
          <span className='text-sm font-semibold text-slate-800'>Round</span>

          <div className='flex gap-1'>
            {roundNumbers.map((r) => (
              <Button
                key={r}
                variant='outline'
                className={`w-5 h-8 text-sm font-bold rounded-lg border transition-all duration-200 ${
                  effectiveCurrentRound === r
                    ? `bg-gradient-to-br ${roundColors.primary} hover:${roundColors.primary} border-${roundColors.accent} text-white shadow-md`
                    : 'bg-white hover:bg-slate-50 border-slate-300 text-slate-700 hover:border-slate-400'
                }`}
              >
                {r}
              </Button>
            ))}
          </div>

          <div className='flex flex-shrink-0 gap-1 items-center ml-auto'>
            <Button
              variant='secondary'
              className='flex justify-center items-center w-8 h-8 bg-white rounded-lg border transition-all duration-200 hover:bg-slate-50 border-slate-300'
              onClick={handlePauseGameWrapper}
            >
              {roundStatus === ActivityState.Paused ? (
                <Play className='w-4 h-4 text-slate-700 fill-slate-700' />
              ) : (
                <Pause className='w-4 h-4 text-slate-700 fill-slate-700' />
              )}
            </Button>

            <Button
              variant='outline'
              className='w-8 h-8 bg-white rounded-lg border transition-all duration-200 hover:bg-slate-50 border-slate-300'
              onClick={handleStartNextRoundWrapper}
              disabled={
                !isGameStarted ||
                isGameCompleted ||
                currentRound === totalRounds
              }
            >
              <ChevronRight className='w-4 h-4 text-slate-700' />
            </Button>
          </div>
        </div>

        {!manualMode && stages.length > 0 && (
          <div className='relative'>
            <div
              className={`bg-gradient-to-r ${roundColors.primary} rounded-xl p-2 shadow-lg`}
            >
              <div className='flex justify-between items-center w-full'>
                <div className='flex flex-1 gap-1 items-center'>
                  {stages.map((stage, index) => (
                    <React.Fragment key={stage.id}>
                      <Button
                        variant='secondary'
                        className={`h-8 text-sm font-medium rounded-lg transition-all duration-200 ${
                          currentPhase === stage.id
                            ? `bg-gradient-to-br ${stage.color} hover:${stage.color} text-white border-2 border-white/50 shadow-md`
                            : 'bg-white/90 hover:bg-white text-slate-700 border border-white/50'
                        }`}
                      >
                        {stage.label}
                      </Button>

                      {index < stages.length - 1 && (
                        <div className='flex justify-center items-center w-2 h-2'>
                          <div className='w-1 h-1 rounded-full bg-white/60'></div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                <div className='flex flex-shrink-0 gap-1 items-center ml-2'>
                  <Button
                    variant='secondary'
                    className='flex justify-center items-center p-0 w-8 h-8 rounded-lg border transition-all duration-200 bg-white/90 hover:bg-white border-white/50'
                    onClick={handleEndPhaseWrapper}
                    disabled={
                      !isGameStarted ||
                      isGameCompleted ||
                      roundStatus === ActivityState.Completed
                    }
                  >
                    <ChevronRight className='w-4 h-4 text-slate-700' />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className='flex gap-3 justify-between items-center text-slate-600'>
          <div className='flex gap-1 items-center'>
            <span className='text-xs font-medium'>
              Round {effectiveCurrentRound}
              {gameMode && ` • ${TimerModeLabels[gameMode as TimerMode]}`}
              {!manualMode && stages.length > 0 && currentPhase && (
                <>
                  {' '}
                  •{' '}
                  {currentPhase.charAt(0).toUpperCase() + currentPhase.slice(1)}
                </>
              )}
            </span>
          </div>

          <TimerDisplay
            manualMode={manualMode}
            roundStatus={roundStatus}
            isInfinitePhase={isInfinitePhase}
            endTime={endTime}
            phaseStatus={phaseStatus}
          />
        </div>
      </div>
    </div>
  )
}

export default GameControlPanel
